import React, { useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router';
import { Box, Typography, CircularProgress, Grid } from '@material-ui/core';
import { ErrorOutline, CheckCircleOutline, Explore, EventNote, Person } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { EditorState, convertFromRaw } from 'draft-js';
import pluralize from 'pluralize';
import { gql } from '@apollo/client';
import { PublicScreenWrapper } from '../../PublicScreenWrapper';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

const GET_FUNNEL_ORG_INFO = gql`
  query GetFunnelOrgInfo {
    orgName
    orgLogo
    tm {
      tenant {
        menuLabels {
          wordProgram
          wordActivity
        }
      }
    }
  }
`;

interface FunnelOrgInfo {
  orgName: string;
  orgLogo: string | null;
  tm: {
    tenant: {
      menuLabels: {
        wordProgram: string | null;
        wordActivity: string | null;
      } | null;
    };
  };
}

interface FunnelItem {
  programId?: string;
  activityId?: string;
  activityRoleId?: string;
  name: string;
}

interface FunnelTask {
  taskId: string;
  title: string;
}

interface FunnelData {
  funnelId: string;
  name: string;
  slug: string;
  description: string | null;
  image: string | null;
  expiresAt: string | null;
  isActive: boolean;
  showTaskList: boolean;
  lockPrograms: boolean;
  lockActivities: boolean;
  lockRoles: boolean;
  programs: FunnelItem[];
  activities: FunnelItem[];
  activityRoles: FunnelItem[];
  tasks: FunnelTask[];
}

function parseDescription(descriptionJson: string | null): EditorState | null {
  if (!descriptionJson) return null;
  try {
    return EditorState.createWithContent(convertFromRaw(JSON.parse(descriptionJson)));
  } catch {
    return null;
  }
}

function FunnelSignupGroup({
  heading,
  programs,
  activities,
  roles,
  programLabel,
  activityLabel,
}: {
  heading: string;
  programs: FunnelItem[];
  activities: FunnelItem[];
  roles: FunnelItem[];
  programLabel: string;
  activityLabel: string;
}) {
  const hasItems = programs.length > 0 || activities.length > 0 || roles.length > 0;
  if (!hasItems) return null;

  const sectionLabelStyle = {
    fontSize: '11px',
    fontWeight: 600,
    color: '#646464',
    textTransform: 'uppercase' as const,
    marginBottom: 4,
  };

  return (
    <Box marginBottom={2}>
      <Typography
        variant='body2'
        style={{
          fontWeight: 600,
          fontSize: '13px',
          color: '#3E3229',
          marginBottom: 8,
        }}
      >
        {heading}
      </Typography>

      {programs.length > 0 && (
        <Box marginBottom={1}>
          <Typography variant='body2' style={sectionLabelStyle}>
            {programLabel}
          </Typography>
          {programs.map((p) => (
            <Box key={p.programId} display='flex' alignItems='center' paddingY={0.5} paddingLeft={0.5}>
              <Explore style={{ fontSize: 16, color: '#9e9e9e', marginRight: 8 }} />
              <Typography variant='body2' style={{ fontSize: '14px', color: '#3E3229' }}>
                {p.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {activities.length > 0 && (
        <Box marginBottom={1}>
          <Typography variant='body2' style={sectionLabelStyle}>
            {activityLabel}
          </Typography>
          {activities.map((a) => (
            <Box key={a.activityId} display='flex' alignItems='center' paddingY={0.5} paddingLeft={0.5}>
              <EventNote style={{ fontSize: 16, color: '#9e9e9e', marginRight: 8 }} />
              <Typography variant='body2' style={{ fontSize: '14px', color: '#3E3229' }}>
                {a.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {roles.length > 0 && (
        <Box marginBottom={1}>
          <Typography variant='body2' style={sectionLabelStyle}>
            Roles
          </Typography>
          {roles.map((r) => (
            <Box key={r.activityRoleId} display='flex' alignItems='center' paddingY={0.5} paddingLeft={0.5}>
              <Person style={{ fontSize: 16, color: '#9e9e9e', marginRight: 8 }} />
              <Typography variant='body2' style={{ fontSize: '14px', color: '#3E3229' }}>
                {r.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export const FunnelScreen = () => {
  const { slug } = useParams<{ slug: string }>();
  const history = useHistory();
  const apiUrl = useApiUrl();
  const { isMobile } = useCampfireTheme();
  const funnelFetch = useCampfireFetch<FunnelData>({ defer: true, withAuth: false });

  const { data: orgData } = useCampfireQuery<FunnelOrgInfo, null>(GET_FUNNEL_ORG_INFO, {});

  const programLabel = pluralize(orgData?.tm?.tenant?.menuLabels?.wordProgram || 'Program');
  const activityLabel = pluralize(orgData?.tm?.tenant?.menuLabels?.wordActivity || 'Activity');

  useEffect(() => {
    funnelFetch.run({
      method: 'get',
      url: `${apiUrl}/vm/funnel/get-by-slug`,
      params: { slug },
    });
  }, [slug]);

  const funnel = funnelFetch.response?.data?.data ?? null;
  const { isLoading } = funnelFetch;
  const hasError = funnelFetch.error !== undefined;
  const isNotFound = !isLoading && !hasError && funnel === null;
  const isExpired = funnel !== null && !funnel.isActive;

  const descriptionState = useMemo(() => {
    if (!funnel?.description) return null;
    return parseDescription(funnel.description);
  }, [funnel?.description]);

  const hasSignupItems = funnel
    ? (funnel.programs?.length ?? 0) > 0 ||
      (funnel.activities?.length ?? 0) > 0 ||
      (funnel.activityRoles?.length ?? 0) > 0
    : false;

  const hasTaskItems = funnel ? (funnel.tasks?.length ?? 0) > 0 : false;
  const hasChecklist = hasSignupItems || hasTaskItems;

  const handleStartApplication = () => {
    history.push(`/apply?funnelId=${funnel?.funnelId}`);
  };

  if (isLoading) {
    return (
      <PublicScreenWrapper hideHeader>
        <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
          <CircularProgress />
        </Box>
      </PublicScreenWrapper>
    );
  }

  if (hasError || isNotFound) {
    return (
      <PublicScreenWrapper hideHeader>
        <Box display='flex' alignItems='center' justifyContent='center' minHeight='60vh' marginLeft={2} marginRight={2}>
          <Grid container justify='center' spacing={3}>
            <Grid item xs={12} sm={10}>
              <Box display='flex' justifyContent='center' alignItems='center' marginBottom={2}>
                <ErrorOutline style={{ fontSize: 48, color: '#f44336' }} />
              </Box>
              <Typography variant='h5' style={{ fontWeight: 'bold' }} align='center' gutterBottom>
                Link not valid
              </Typography>
              <Typography variant='body1' align='center' color='textSecondary'>
                This application link is not valid or has been removed.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </PublicScreenWrapper>
    );
  }

  if (isExpired) {
    return (
      <PublicScreenWrapper hideHeader>
        <Box display='flex' alignItems='center' justifyContent='center' minHeight='60vh' marginLeft={2} marginRight={2}>
          <Grid container justify='center' spacing={3}>
            <Grid item xs={12} sm={10}>
              <Box display='flex' justifyContent='center' alignItems='center' marginBottom={2}>
                <ErrorOutline style={{ fontSize: 48, color: '#ff9800' }} />
              </Box>
              <Typography variant='h5' style={{ fontWeight: 'bold' }} align='center' gutterBottom>
                Application closed
              </Typography>
              <Typography variant='body1' align='center' color='textSecondary'>
                This application is no longer accepting responses.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </PublicScreenWrapper>
    );
  }

  return (
    <PublicScreenWrapper hideHeader shrink={isMobile ? true : 720}>
      <Box
        marginTop={isMobile ? 1 : 2}
        marginBottom={0}
        paddingX={isMobile ? 2 : 4}
        paddingTop={isMobile ? 2 : 3}
        paddingBottom={isMobile ? 2 : 3}
      >
        {/* Org header */}
        <Box marginBottom={3} paddingLeft={1}>
          {orgData?.orgLogo && (
            <Box marginBottom={3}>
              <img
                src={orgData.orgLogo}
                alt={orgData.orgName}
                style={{
                  height: 65,
                  width: 'auto',
                  display: 'block',
                }}
              />
            </Box>
          )}
          <Typography
            variant='h5'
            style={{
              color: '#3E3229',
              fontSize: '22px',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              marginBottom: 4,
            }}
          >
            {funnel?.name}
          </Typography>
          {orgData?.orgName && (
            <Typography
              variant='body1'
              style={{
                color: '#646464',
                fontSize: '16px',
                fontWeight: 400,
              }}
            >
              {orgData.orgName}
            </Typography>
          )}
        </Box>

        {/* Banner image */}
        {funnel?.image && (
          <Box marginBottom={3} borderRadius={8} overflow='hidden'>
            <img
              src={funnel.image}
              alt={funnel.name}
              style={{
                width: '100%',
                maxHeight: 280,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        )}

        {/* Separator */}
        <Box height={2} borderRadius={4} style={{ backgroundColor: '#f0f0f0' }} marginY={3} />

        {/* Description */}
        {descriptionState && (
          <Box marginBottom={3} paddingLeft={1}>
            <CampfireRichEditor editorState={descriptionState} readOnly />
          </Box>
        )}

        {/* Checklist columns */}
        {funnel?.showTaskList && hasChecklist && (
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            {/* Left column: What you'll be signing up for */}
            {hasSignupItems && (
              <Grid item xs={12} sm={hasTaskItems ? 6 : 12}>
                <Box
                  padding={2.5}
                  borderRadius={8}
                  height='100%'
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                  }}
                >
                  <FunnelSignupGroup
                    heading='What you will apply for'
                    programs={funnel.lockPrograms ? funnel.programs : []}
                    activities={funnel.lockActivities ? funnel.activities : []}
                    roles={funnel.lockRoles ? funnel.activityRoles : []}
                    programLabel={programLabel}
                    activityLabel={activityLabel}
                  />
                  <FunnelSignupGroup
                    heading='What you can apply for'
                    programs={!funnel.lockPrograms ? funnel.programs : []}
                    activities={!funnel.lockActivities ? funnel.activities : []}
                    roles={!funnel.lockRoles ? funnel.activityRoles : []}
                    programLabel={programLabel}
                    activityLabel={activityLabel}
                  />
                </Box>
              </Grid>
            )}

            {/* Right column: What you need to complete */}
            {hasTaskItems && (
              <Grid item xs={12} sm={hasSignupItems ? 6 : 12}>
                <Box
                  padding={2.5}
                  borderRadius={8}
                  height='100%'
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                  }}
                >
                  <Typography
                    variant='body2'
                    style={{
                      fontWeight: 600,
                      fontSize: '13px',
                      color: '#3E3229',
                      marginBottom: 12,
                    }}
                  >
                    What you need to complete
                  </Typography>

                  {funnel.tasks.map((t) => (
                    <Box key={t.taskId} display='flex' alignItems='center' paddingY={0.5} paddingLeft={0.5}>
                      <CheckCircleOutline style={{ fontSize: 16, color: '#4caf50', marginRight: 8 }} />
                      <Typography variant='body2' style={{ fontSize: '14px', color: '#3E3229' }}>
                        {t.title}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {/* Separator */}
        <Box height={2} borderRadius={4} style={{ backgroundColor: '#f0f0f0' }} marginY={3} />

        {/* CTA */}
        <Box display='flex' justifyContent='flex-end'>
          <TabletButton
            variant='contained'
            color='primary'
            size='large'
            onClick={handleStartApplication}
            style={{ paddingLeft: 48, paddingRight: 48 }}
          >
            Start Application
          </TabletButton>
        </Box>
      </Box>
    </PublicScreenWrapper>
  );
};
